import i18n from '@/locales/i18n';
import { showCreditClaimToast } from '@/composables/useCreditClaimToast';
import {
  ENABLE_DEPOSIT_BONUS_CAMPAIGN,
  resolveDepositBonus,
} from '@/domain/deposit/deposit-bonus-campaign';
import { getUserId } from '@/utils/auth-token.js';

function readErrMessage(err) {
  return String(err?.message ?? err?.msg ?? '').trim();
}

/** 首次充值 pending toast 后，短暂抑制「领取成功」避免文案打架 */
let firstDepositCreditsPendingAt = 0;
const FIRST_DEPOSIT_CLAIM_TOAST_SUPPRESS_MS = 90_000;

/** 每用户只弹一次「一分钟内到账」（与埋点 first 分离，防并发重复） */
const PENDING_TOAST_SEEN_PREFIX = 'ds-first-deposit-credits-pending-toast:';

function pendingToastSeenKey() {
  const uid = String(getUserId() ?? '').trim();
  return uid ? `${PENDING_TOAST_SEEN_PREFIX}${uid}` : `${PENDING_TOAST_SEEN_PREFIX}anon`;
}

function hasPendingToastSeen() {
  if (typeof localStorage === 'undefined') return true;
  try {
    return localStorage.getItem(pendingToastSeenKey()) === '1';
  } catch {
    return true;
  }
}

function markPendingToastSeen() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(pendingToastSeenKey(), '1');
  } catch {
    /* ignore */
  }
}

function shouldSuppressImmediateCreditClaimToast() {
  return (
    firstDepositCreditsPendingAt > 0 &&
    Date.now() - firstDepositCreditsPendingAt < FIRST_DEPOSIT_CLAIM_TOAST_SUPPRESS_MS
  );
}

/**
 * 首次充值到账：体验金 / 奖励金将在约一分钟内入账。
 * 每用户持久只弹一次；已展示过则直接返回。
 */
export function notifyFirstDepositCreditsPending() {
  if (hasPendingToastSeen()) return;
  markPendingToastSeen();
  firstDepositCreditsPendingAt = Date.now();
  try {
    showCreditClaimToast(i18n.global.t('deposit.firstDepositCreditsPendingToast'), {
      dedupeKey: 'first-deposit-credits-pending',
      durationMs: 3600,
    });
  } catch {
    /* i18n soft-fail */
  }
}

/** 体验金到账：居中文字弹窗 */
export function notifyTrialFundAutoClaimSuccess() {
  if (shouldSuppressImmediateCreditClaimToast()) return;
  try {
    showCreditClaimToast(i18n.global.t('trialFund.claimSuccessToast'), {
      dedupeKey: 'trial-fund-claim-success',
    });
  } catch {
    /* i18n soft-fail */
  }
}

/** 充值奖励金到账：居中文字弹窗 */
export function notifyDepositBonusClaimSuccess() {
  if (shouldSuppressImmediateCreditClaimToast()) return;
  try {
    showCreditClaimToast(i18n.global.t('deposit.bonusClaimSuccessToast'), {
      dedupeKey: 'deposit-bonus-claim-success',
    });
  } catch {
    /* i18n soft-fail */
  }
}

/** 每笔入账订单只弹一次「奖励金领取成功」（防 success 多路径 / 回跳重复） */
const BONUS_CLAIM_TOAST_SEEN_PREFIX = 'ds.depositBonusClaimToast.';

function bonusClaimToastSeenKey(orderId, amount, fingerprint) {
  const uid = String(getUserId() ?? '').trim() || 'anon';
  const oid = String(orderId ?? '').trim();
  if (oid) return `${BONUS_CLAIM_TOAST_SEEN_PREFIX}${uid}.${oid}`;
  const fp = String(fingerprint ?? '').trim();
  if (fp) return `${BONUS_CLAIM_TOAST_SEEN_PREFIX}${uid}.fp.${fp}`;
  const amt = Number(amount);
  const amtKey = Number.isFinite(amt) ? String(amt) : 'na';
  return `${BONUS_CLAIM_TOAST_SEEN_PREFIX}${uid}.amt.${amtKey}`;
}

function hasBonusClaimToastSeen(orderId, amount, fingerprint) {
  if (typeof localStorage === 'undefined') return true;
  try {
    return localStorage.getItem(bonusClaimToastSeenKey(orderId, amount, fingerprint)) === '1';
  } catch {
    return true;
  }
}

function markBonusClaimToastSeen(orderId, amount, fingerprint) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(bonusClaimToastSeenKey(orderId, amount, fingerprint), '1');
  } catch {
    /* ignore */
  }
}

/**
 * 入账金额命中充值赠送档时弹出「奖励金领取成功」。
 * @param {unknown} usdAmount
 * @param {{ orderId?: unknown, txHash?: unknown, itemId?: unknown }} [meta]
 */
export function notifyDepositBonusClaimSuccessIfEligible(usdAmount, meta = {}) {
  if (!ENABLE_DEPOSIT_BONUS_CAMPAIGN) return;
  if (!resolveDepositBonus(usdAmount)) return;
  const orderId = meta?.orderId;
  const fingerprint = meta?.txHash || meta?.itemId || '';
  if (hasBonusClaimToastSeen(orderId, usdAmount, fingerprint)) return;
  markBonusClaimToastSeen(orderId, usdAmount, fingerprint);
  notifyDepositBonusClaimSuccess();
}

/**
 * Map trial claim API errors to user-facing copy (never raw「系统内部错误 (500)」).
 * @param {unknown} err
 * @returns {string}
 */
export function resolveTrialClaimUserMessage(err) {
  const t = i18n.global.t;
  const raw = readErrMessage(err);
  const code = Number(err?.code);

  if (err?.code === 'BOT_PROOF_POW_UNAVAILABLE') {
    return t('trialFund.claimRetryLater');
  }
  if (/already\s*claim|已经领取/i.test(raw)) {
    return '';
  }
  if (
    code === 400 &&
    /首次充值|deposit_required|trial\.deposit_required|再领取体验金|complete.*deposit.*claim/i.test(
      raw
    )
  ) {
    return t('trialFund.claimNeedDeposit');
  }
  if (/首次充值|deposit_required|trial\.deposit_required|再领取体验金/i.test(raw)) {
    return t('trialFund.claimNeedDeposit');
  }
  if (code === 503 || /grant\s*pending|正在发放|mint_pending|try\s*again/i.test(raw)) {
    return t('trialFund.claimPending');
  }
  if (/timeout|504|gateway\s*timeout/i.test(raw)) {
    return t('trialFund.claimPending');
  }
  if (/internal|系统内部|\(500\)/i.test(raw)) {
    return t('trialFund.claimRetryLater');
  }
  if (raw) {
    return raw;
  }
  return t('trialFund.claimRetryLater');
}

/**
 * @param {unknown} err
 * @returns {boolean}
 */
export function isTrialClaimDepositRequiredError(err) {
  const raw = readErrMessage(err);
  if (
    Number(err?.code) === 400 &&
    /首次充值|deposit_required|trial\.deposit_required|再领取体验金/i.test(raw)
  ) {
    return true;
  }
  return /首次充值|deposit_required|trial\.deposit_required|再领取体验金/i.test(raw);
}
